import { useEffect, useRef } from 'react'

// Canvas firework particle system for Tet greeting overlay
// Rockets launch from bottom, explode into radial particles with gravity + fade

const COLORS = [
    '#ff4444',
    '#ffdd44',
    '#ff8844',
    '#ff4488',
    '#ffaa44',
    '#ff6644',
    '#ffcc44',
    '#ff4466',
    '#ffe066',
    '#ff6688',
]
const PARTICLE_COUNT = 40 // particles per explosion
const GRAVITY = 0.03
const LAUNCH_INTERVAL = 50 // ms between launches
const INITIAL_DELAY = 1500 // ms before first firework (after banner split)

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    alpha: number
    decay: number
    color: string
    size: number
}

interface Rocket {
    x: number
    y: number
    vy: number
    targetY: number
    exploded: boolean
    particles: Particle[]
    trail: { x: number; y: number; alpha: number }[]
}

const createRocket = (w: number, h: number): Rocket => ({
    x: w * (0.1 + Math.random() * 0.8),
    y: h,
    vy: -(3 + Math.random() * 2),
    targetY: h * (0.15 + Math.random() * 0.3),
    exploded: false,
    particles: [],
    trail: [],
})

const explode = (rocket: Rocket) => {
    rocket.exploded = true
    const color = COLORS[Math.floor(Math.random() * COLORS.length)]
    for (let i = 0; i < PARTICLE_COUNT; i++) {
        const angle = (Math.PI * 2 * i) / PARTICLE_COUNT
        const speed = 1 + Math.random() * 2
        rocket.particles.push({
            x: rocket.x,
            y: rocket.y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            alpha: 1,
            decay: 0.01 + Math.random() * 0.015,
            color:
                Math.random() > 0.3
                    ? color
                    : COLORS[Math.floor(Math.random() * COLORS.length)],
            size: 1.5 + Math.random() * 1.5,
        })
    }
}

const TetFireworksCanvas = ({ active }: { active: boolean }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        if (!active) return
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')!

        let w = window.innerWidth
        let h = window.innerHeight
        canvas.width = w
        canvas.height = h

        const rockets: Rocket[] = []
        let animId: number
        let launchTimer: ReturnType<typeof setTimeout>

        const launchOne = () => {
            rockets.push(createRocket(w, h))
        }

        const startLaunching = () => {
            launchOne()
            const scheduleNext = () => {
                launchTimer = setTimeout(
                    () => {
                        launchOne()
                        scheduleNext()
                    },
                    LAUNCH_INTERVAL + Math.random() * 800
                )
            }
            scheduleNext()
        }

        // Delay first firework until after banner animation
        const delayTimer = setTimeout(startLaunching, INITIAL_DELAY)

        const draw = () => {
            ctx.clearRect(0, 0, w, h)

            for (let i = rockets.length - 1; i >= 0; i--) {
                const r = rockets[i]

                if (!r.exploded) {
                    // Update rocket position
                    r.y += r.vy
                    // Trail
                    r.trail.push({ x: r.x, y: r.y, alpha: 1 })
                    if (r.trail.length > 8) r.trail.shift()

                    // Draw trail
                    for (const t of r.trail) {
                        ctx.beginPath()
                        ctx.arc(t.x, t.y, 1.5, 0, Math.PI * 2)
                        ctx.fillStyle = `rgba(255, 220, 150, ${t.alpha})`
                        ctx.fill()
                        t.alpha -= 0.12
                    }

                    // Draw rocket head
                    ctx.beginPath()
                    ctx.arc(r.x, r.y, 2, 0, Math.PI * 2)
                    ctx.fillStyle = '#ffe8aa'
                    ctx.fill()

                    // Explode at target height
                    if (r.y <= r.targetY) explode(r)
                } else {
                    // Update and draw particles
                    let allDead = true
                    for (const p of r.particles) {
                        p.x += p.vx
                        p.y += p.vy
                        p.vy += GRAVITY
                        p.alpha -= p.decay
                        if (p.alpha <= 0) continue
                        allDead = false

                        ctx.beginPath()
                        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
                        ctx.fillStyle = p.color
                        ctx.globalAlpha = p.alpha
                        ctx.fill()
                        ctx.globalAlpha = 1
                    }

                    // Remove dead rockets
                    if (allDead) rockets.splice(i, 1)
                }
            }

            animId = requestAnimationFrame(draw)
        }
        draw()

        const onResize = () => {
            w = window.innerWidth
            h = window.innerHeight
            canvas.width = w
            canvas.height = h
        }
        window.addEventListener('resize', onResize)

        return () => {
            cancelAnimationFrame(animId)
            clearTimeout(delayTimer)
            clearTimeout(launchTimer)
            window.removeEventListener('resize', onResize)
        }
    }, [active])

    if (!active) return null

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none z-20"
        />
    )
}

export default TetFireworksCanvas
